import React, { useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import { Flex, Grid } from "@domestika-dev/prudencio/dist/atoms/Layout";
import { Tooltip } from "@domestika-dev/prudencio/dist/atoms/Tooltip";
import { LiveSessionContext } from "../../../context/LiveSessionContext";
import { config } from "../../../config";
import { ChatFooterInput } from "./ChatFooterInput";
import { ChatFooterInputCounter } from "./ChatFooterInputCounter";
import { ChatFooterSendButton } from "./ChatFooterSendButton";

export const ChatFooter = ({ footerHeight }) => {
  const { errorSendingChatMessage, onSendMessage } = useContext(LiveSessionContext);

  const intl = useIntl();
  const [chatSendButtonDisabled, setChatSendButtonDisabled] = useState(true);
  const [chatMessage, setChatMessage] = useState();
  const [showTooltip, setShowTooltip] = useState();
  const [tooltipMessage, setTooltipMessage] = useState();
  const [relativeElementPosition, setRelativeElementPosition] = useState({});
  const chatFooterRef = useRef();

  const getRelativeElementPosition = () => ({
    bottom: relativeElementPosition.bottom,
    height: relativeElementPosition.height,
    left: relativeElementPosition.left,
    right: relativeElementPosition.right,
    top: relativeElementPosition.top - 50,
    width: relativeElementPosition.width,
    x: relativeElementPosition.x,
    y: relativeElementPosition.y,
  });

  const onMessageChangeHandler = (message) => {
    setChatMessage(message);
    setChatSendButtonDisabled(!message?.length);
  };

  const handleSendMessage = () => {
    onSendMessage(chatMessage.trim());
    setChatMessage("");
    setChatSendButtonDisabled(true);
    setShowTooltip(false);
  };

  const setupTooltip = ({ tooltipText }) => {
    setShowTooltip(true);
    setRelativeElementPosition(chatFooterRef?.current?.getBoundingClientRect());
    setTooltipMessage(tooltipText);
  };

  useEffect(() => {
    setShowTooltip(false);
    if (chatMessage?.length === config.MAX_LIVE_CHAT_MESSAGE_LENGTH) {
      setupTooltip({
        tooltipText: intl.formatMessage(
          {
            id: "live_sessions.chat.footer.input.error_max_length",
          },
          {
            max_length: config.MAX_LIVE_CHAT_MESSAGE_LENGTH,
          }
        ),
      });
    }
  }, [chatMessage]);

  useEffect(() => {
    if (errorSendingChatMessage) {
      setupTooltip({
        tooltipText: intl.formatMessage({
          id: "live_sessions.chat.footer.input.error_sending",
        }),
      });
    }
  }, [errorSendingChatMessage]);

  return (
    <Flex
      justifyContent="center"
      borderBottom="1px solid"
      borderTop="1px solid"
      borderLeft="1px solid"
      borderColor="durian"
      height={`${footerHeight}px`}
    >
      {showTooltip && (
        <Tooltip
          relativeElementPosition={getRelativeElementPosition()}
          message={tooltipMessage}
          id="liveChatFooterTooltip"
        />
      )}
      <Grid style={{ width: "100%" }} padding={[4, 4, 4]} gridRowGap={2} ref={chatFooterRef}>
        <ChatFooterInput
          chatMessage={chatMessage}
          onMessageChangeHandler={onMessageChangeHandler}
          onSendMessage={handleSendMessage}
        />
        <ChatFooterInputCounter messageLength={chatMessage?.length} />
        <ChatFooterSendButton onSendMessage={handleSendMessage} disabled={chatSendButtonDisabled} />
      </Grid>
    </Flex>
  );
};

ChatFooter.propTypes = {
  footerHeight: PropTypes.number,
};

ChatFooter.defaultProps = {
  footerHeight: 0,
};

export default ChatFooter;
